import { useState } from "react";
import { Calculator } from "lucide-react";
import { fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SHIPPING_GUIDE = [
  { name: "Supplement", amount: 100000 },
  { name: "Baju", amount: 100000 },
  { name: "Jaket", amount: 250000 },
  { name: "Cardigan", amount: 150000 },
  { name: "Celana", amount: 200000 },
  { name: "Sneakers", amount: 450000 },
  { name: "Tas", amount: 350000 },
  { name: "Large Tas", amount: 450000 },
  { name: "Dompet Biasa", amount: 250000 },
  { name: "Dompet Kartu", amount: 150000 },
];

// < $100 -> flat $10, >= $100 -> 10% of item price
export const serviceFeeUSD = (price) => (price < 100 ? 10 : Math.round(price * 10) / 100);

// Fixed USA sales tax — permanently 9%, always applied, never configurable
export const USA_TAX_RATE = 0.09;
export const usaTaxUSD = (price) => Math.round(price * USA_TAX_RATE * 100) / 100;

const fmtUSD = (v) => {
  const r = Math.round(v * 100) / 100;
  return `$${r.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 })}`;
};

export default function USACalculator() {
  const [price, setPrice] = useState("");
  const [rate, setRate] = useState(""); // digits only; displayed with Indonesian thousands dots
  const [shipping, setShipping] = useState("");

  const priceNum = parseFloat(price);
  const hasPrice = price !== "" && !Number.isNaN(priceNum) && priceNum >= 0;
  const rateNum = parseInt(rate || "", 10);
  const hasRate = rate !== "" && !Number.isNaN(rateNum) && rateNum > 0;
  const shipNum = shipping === "" ? 0 : Math.max(0, parseInt(shipping, 10) || 0);

  const fee = hasPrice ? serviceFeeUSD(priceNum) : null;
  const feeLabel = hasPrice ? (priceNum < 100 ? "Flat fee (< $100)" : "10% fee") : null;
  const tax = hasPrice ? usaTaxUSD(priceNum) : null;
  const totalUSD = hasPrice ? priceNum + tax + fee : null;
  const convertedIDR = hasPrice && hasRate ? totalUSD * rateNum : null;
  const finalIDR = convertedIDR != null ? convertedIDR + shipNum : null;

  return (
    <div data-testid="usa-calculator" className="overflow-hidden rounded-lg border bg-card">
      <div className="flex items-center gap-2.5 border-b px-4 py-3" style={{ backgroundColor: "#D8C9BE" }}>
        <Calculator className="h-4 w-4 shrink-0" style={{ color: "#4E4542" }} />
        <div>
          <h2 className="font-heading text-sm font-extrabold tracking-wide" style={{ color: "#4E4542" }}>🇺🇸 USA CALCULATOR</h2>
          <p className="text-[11px]" style={{ color: "#6B615C" }}>Standalone — no USA batch needed</p>
        </div>
      </div>
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">ITEM PRICE (USD)</Label>
            <NumInput
              data-testid="usa-price-input"
              mode="decimal"
              className="h-12"
              placeholder="0.00"
              value={price}
              onValue={setPrice}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">SERVICE FEE (auto)</Label>
            <div data-testid="usa-service-fee" className="flex h-12 flex-col justify-center rounded-md border bg-accent px-3">
              {hasPrice ? (
                <>
                  <span className="text-sm font-extrabold leading-tight">{fmtUSD(fee)}</span>
                  <span className="text-[10px] font-semibold text-muted-foreground">{feeLabel}</span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">USD RATE (Rp / $1)</Label>
            <NumInput
              data-testid="usa-rate-input"
              mode="money"
              className="h-12"
              placeholder="e.g. 16.300"
              value={rate}
              onValue={setRate}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">SHIPPING (Rp)</Label>
            <NumInput
              data-testid="usa-shipping-input"
              mode="money"
              className="h-12"
              placeholder="e.g. 450.000"
              value={shipping}
              onValue={setShipping}
            />
          </div>
        </div>

        <div data-testid="usa-shipping-guide" className="rounded-md border border-dashed p-3" style={{ backgroundColor: "#EDE4DE" }}>
          <p className="mb-2 text-[11px] font-extrabold tracking-wide text-stone-500">SHIPPING GUIDE</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {SHIPPING_GUIDE.map((g) => (
              <div key={g.name} data-testid={`usa-guide-${g.name.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center justify-between text-xs">
                <span className="text-stone-600">{g.name}</span>
                <span className="font-semibold text-stone-800">{fmtIDR(g.amount)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] italic text-muted-foreground">Nominal di atas hanya panduan. Sesuaikan ongkir jika barang lebih berat/besar.</p>
        </div>

        <div className="space-y-1.5 border-t pt-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">USA TAX (9%)</span>
            <span data-testid="usa-tax" className="font-bold">{tax != null ? fmtUSD(tax) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">ITEM + TAX + FEE</span>
            <span data-testid="usa-total-usd" className="font-bold">{totalUSD != null ? fmtUSD(totalUSD) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">CONVERTED TO IDR</span>
            <span data-testid="usa-converted-idr" className="font-bold">{convertedIDR != null ? fmtIDR(convertedIDR) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">SHIPPING</span>
            <span data-testid="usa-shipping-display" className="font-bold">{shipping !== "" ? fmtIDR(shipNum) : "—"}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between rounded-md border border-[#C7D5DB] px-3 py-2.5" style={{ backgroundColor: "#DCE5E9" }}>
            <span className="text-xs font-extrabold tracking-wide" style={{ color: "#3F5560" }}>FINAL H&T COST</span>
            <span data-testid="usa-final-cost" className="font-heading text-xl font-extrabold" style={{ color: "#2F4A57" }}>
              {finalIDR != null ? fmtIDR(finalIDR) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
